import { axios } from "@tlc/ls2pac/components/AxiosInstanceManager";
import { SearchParametersDwr } from "../ls/LSSearch";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { BritannicaResultRecord } from "./BritannicaResultRecord";
import { BritannicaSearchResponse } from "./BritannicaSearchResponse";

interface BritannicaSearchContextType {
  /**
   * @param page 1 based page index
   */
  setPage: (page: number) => void;
  searchParams: { page: number; term: string };
  totalHits: Readonly<number | null>;
  results: Readonly<BritannicaResultRecord[] | null>;
  resultsKey: Readonly<string | null>;
  loadingResults: Readonly<boolean>;
  pageSize: Readonly<number>;
}

const BritannicaSearchContext = createContext<
  BritannicaSearchContextType | undefined
>(undefined);

export const useBritannicaSearch = () => {
  const ctx = useContext(BritannicaSearchContext);
  if (!ctx)
    throw new Error(
      "useBritannicaSearch must be used within BritannicaSearchProvider"
    );
  return ctx;
};

export const BritannicaSearchProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loadingResults, setLoadingResults] = useState<boolean>(false);
  const [totalHits, setTotalHits] = useState<number | null>(null);
  const [results, setResults] = useState<BritannicaResultRecord[] | null>(null);
  const pageSize = 12;

  const resultsKey = useMemo(() => {
    if (!results) {
      return null;
    }
    return results
      .map((r) => r.tocId)
      .sort()
      .toString();
  }, [results]);

  const state = useMemo(() => {
    const term = searchParams.get("term") || "";
    const pageParam = parseInt(searchParams.get("page") || "1", 10);
    const page = pageParam < 1 ? 1 : pageParam;
    return {
      term,
      page,
    };
  }, [searchParams]);

  useEffect(() => {
    if (!state) {
      return;
    }
    setLoadingResults(true);
    const { term, page } = state;
    search(term, page).then((response) => {
      // TODO set count on parent count provider
      setTotalHits(response.totalHits);
      setResults(response.results);
      setLoadingResults(false);
    });
  }, [state.page, state.term]);

  const search = async (term: string, page: number) => {
    const startIndex = page <= 1 ? 1 : (page - 1) * pageSize + 1;
    const payload: SearchParametersDwr = {
      searchTerm: term,
      hitsPerPage: pageSize,
      startIndex,
      sortCriteria: "",
    };
    try {
      const { data } = await axios.post<BritannicaSearchResponse>(
        `/britannica/search`,
        payload
      );
      return data;
    } catch (e: unknown) {
      return {
        success: false,
        totalHits: 0,
        results: [],
      };
    }
  };

  const setPage = (page: number) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (page <= 1) {
        newParams.delete("page");
      } else {
        newParams.set("page", page.toString());
      }
      return newParams;
    });
  };

  const contextValue: BritannicaSearchContextType = useMemo(() => {
    return {
      searchParams: state,
      setPage,
      totalHits,
      results,
      resultsKey,
      loadingResults,
      pageSize,
    };
  }, [state, totalHits, loadingResults, resultsKey]);

  return (
    <BritannicaSearchContext.Provider value={contextValue}>
      {children}
    </BritannicaSearchContext.Provider>
  );
};
